import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Hash, Users, Search, Send, Smile, Paperclip, AtSign,
  MoreHorizontal, Bell, Settings, Plus, MessageCircle,
  Circle, ChevronDown, Heart, ThumbsUp, Megaphone,
  AlertTriangle, Stethoscope, Crown
} from "lucide-react";

const CHANNEL_ICONS = {
  hash: Hash,
  megaphone: Megaphone,
  stethoscope: Stethoscope,
  "alert-triangle": AlertTriangle,
  heart: Heart,
  users: Users,
  crown: Crown
};

export default function TeamChat() {
  const { user } = useAuth();
  const [channels, setChannels] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const messagesEndRef = useRef(null);
  
  const userId = user?.id || "demo_user";

  useEffect(() => {
    loadChannels();
    loadOnlineUsers();
  }, []);

  useEffect(() => {
    if (selectedChannel) {
      loadMessages(selectedChannel.id);
    }
  }, [selectedChannel]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const loadChannels = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/phase25/chat/channels?user_id=${userId}`);
      const channelList = res.data.channels || [];
      setChannels(channelList);
      if (channelList.length > 0 && !selectedChannel) {
        setSelectedChannel(channelList[0]);
      }
    } catch (error) {
      console.error("Failed to load channels:", error);
      toast.error("Failed to load channels");
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (channelId) => {
    try {
      const res = await api.get(`/phase25/chat/channels/${channelId}/messages?limit=50`);
      setMessages(res.data.messages || []);
      
      // Mark channel as read
      await api.post(`/phase25/chat/channels/${channelId}/read?user_id=${userId}`);
    } catch (error) {
      console.error("Failed to load messages:", error);
    }
  };

  const loadOnlineUsers = async () => {
    try {
      const res = await api.get("/phase25/chat/presence/online");
      setOnlineUsers(res.data.users || []);
    } catch (error) {
      console.error("Failed to load online users:", error);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedChannel) return;
    
    setSending(true);
    try {
      const res = await api.post(
        `/phase25/chat/channels/${selectedChannel.id}/messages?sender_id=${userId}`,
        { content: newMessage.trim() }
      );
      setMessages(prev => [...prev, res.data]);
      setNewMessage("");
    } catch (error) {
      console.error("Failed to send message:", error);
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diff = now - date;
      
      if (diff < 60000) return "Just now";
      if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
      if (diff < 86400000) return date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
      return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    } catch {
      return "";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="chat-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100" data-testid="team-chat">
      <div className="h-screen flex">
        {/* Sidebar */}
        <div className="w-64 bg-slate-800 text-white flex flex-col">
          {/* Workspace Header */}
          <div className="p-4 border-b border-slate-700">
            <div className="flex items-center justify-between">
              <h1 className="font-bold text-lg">Goodwin Cares</h1>
              <Button variant="ghost" size="icon" className="text-slate-400 hover:text-white">
                <ChevronDown className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Channels */}
          <ScrollArea className="flex-1 p-2">
            <div className="mb-4">
              <div className="flex items-center justify-between px-2 mb-2">
                <span className="text-xs font-semibold text-slate-400 uppercase">Channels</span>
                <Button variant="ghost" size="icon" className="h-5 w-5 text-slate-400 hover:text-white">
                  <Plus className="h-3 w-3" />
                </Button>
              </div>
              
              {channels.map((channel) => {
                const IconComponent = CHANNEL_ICONS[channel.icon] || Hash;
                const isSelected = selectedChannel?.id === channel.id;
                
                return (
                  <button
                    key={channel.id}
                    onClick={() => setSelectedChannel(channel)}
                    className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm ${
                      isSelected 
                        ? "bg-blue-600 text-white" 
                        : "text-slate-300 hover:bg-slate-700"
                    }`}
                    data-testid={`channel-${channel.id}`}
                  >
                    <IconComponent className="h-4 w-4" />
                    <span className="flex-1 text-left truncate">{channel.name}</span>
                    {channel.unread_count > 0 && (
                      <Badge className="bg-red-500 text-white text-xs px-1.5">
                        {channel.unread_count}
                      </Badge>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Online Users */}
            <div>
              <div className="flex items-center justify-between px-2 mb-2">
                <span className="text-xs font-semibold text-slate-400 uppercase">Online ({onlineUsers.length})</span>
              </div>
              
              {onlineUsers.slice(0, 8).map((onlineUser) => (
                <button
                  key={onlineUser.user_id}
                  className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm text-slate-300 hover:bg-slate-700"
                >
                  <div className="relative">
                    <Avatar className="h-6 w-6">
                      <AvatarFallback className="text-xs">{onlineUser.name?.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <Circle className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 fill-green-500 text-green-500" />
                  </div>
                  <span className="truncate">{onlineUser.name}</span>
                </button>
              ))}
            </div>
          </ScrollArea>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col bg-white">
          {/* Channel Header */}
          <div className="h-14 border-b flex items-center justify-between px-4">
            <div className="flex items-center gap-2">
              {selectedChannel && (
                <>
                  <Hash className="h-5 w-5 text-slate-500" />
                  <h2 className="font-semibold">{selectedChannel.name}</h2>
                  <Badge variant="outline" className="text-xs">
                    {selectedChannel.member_count} members
                  </Badge>
                </>
              )}
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 h-8 w-48"
                />
              </div>
              <Button variant="ghost" size="icon">
                <Bell className="h-5 w-5" />
              </Button>
            </div>
          </div>

          {/* Messages Area */}
          <ScrollArea className="flex-1 p-4">
            <div className="space-y-4">
              {messages.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <MessageCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No messages yet</p>
                  <p className="text-sm">Be the first to send a message!</p>
                </div>
              ) : (
                messages.map((message, index) => {
                  const showAvatar = index === 0 || 
                    messages[index - 1]?.sender_id !== message.sender_id;
                  
                  return (
                    <div
                      key={message.id}
                      className={`flex gap-3 ${showAvatar ? "mt-4" : "mt-1"}`}
                      data-testid={`message-${message.id}`}
                    >
                      {showAvatar ? (
                        <Avatar className="h-9 w-9">
                          <AvatarFallback className="bg-primary text-white">
                            {message.sender_name?.charAt(0) || "?"}
                          </AvatarFallback>
                        </Avatar>
                      ) : (
                        <div className="w-9" />
                      )}
                      <div className="flex-1">
                        {showAvatar && (
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="font-semibold text-sm">{message.sender_name}</span>
                            <span className="text-xs text-muted-foreground">
                              {formatTime(message.created_at)}
                            </span>
                          </div>
                        )}
                        <p className="text-sm">{message.content}</p>
                        
                        {/* Reactions */}
                        {message.reactions && Object.keys(message.reactions).length > 0 && (
                          <div className="flex gap-1 mt-1">
                            {Object.entries(message.reactions).map(([emoji, users]) => (
                              <button
                                key={emoji}
                                className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-xs hover:bg-slate-200"
                              >
                                <span>{emoji}</span>
                                <span>{users.length}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>
          </ScrollArea>

          {/* Message Input */}
          <div className="p-4 border-t">
            <div className="flex items-end gap-2">
              <Button variant="ghost" size="icon" className="text-slate-500">
                <Plus className="h-5 w-5" />
              </Button>
              <div className="flex-1 relative">
                <Textarea
                  placeholder={`Message #${selectedChannel?.name || "channel"}`}
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="min-h-[40px] max-h-[120px] resize-none pr-24"
                  rows={1}
                  data-testid="message-input"
                />
                <div className="absolute right-2 bottom-2 flex items-center gap-1">
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-500">
                    <AtSign className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-500">
                    <Smile className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-500">
                    <Paperclip className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <Button
                onClick={sendMessage}
                disabled={!newMessage.trim() || sending}
                className="bg-green-600 hover:bg-green-700"
                data-testid="send-button"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Press Enter to send, Shift+Enter for new line
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
